import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

function Card({ title, action, children, testId }) {
  return (
    <div className="card-luxe p-5 md:p-6" data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4 gap-3">
          {title && <h3 className="font-serif text-2xl text-gold">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function copy(text) {
  try { navigator.clipboard.writeText(text); toast.success("Copied"); } catch {}
}

/* ===================== REVIEWS TAB ===================== */
export function ReviewsTab({ settings, saveSettings, onSiteReload, reloadSettings }) {
  const [pending, setPending] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [hint, setHint] = useState("");
  const [dur, setDur] = useState("");
  const [enabled, setEnabled] = useState(!!settings.testimonials?.enabled);
  const [items, setItems] = useState(settings.testimonials?.items || []);

  const load = async () => {
    setPending((await api.adminListPendingReviews()).items);
    setTokens((await api.adminListReviewTokens()).items);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    setEnabled(!!settings.testimonials?.enabled);
    setItems(settings.testimonials?.items || []);
  }, [settings]);

  const sampleCount = items.filter(i => i.is_sample).length;

  const genLink = async () => {
    const t = await api.adminCreateReviewToken({ hint, expected_duration: dur });
    setHint(""); setDur("");
    copy(`${window.location.origin}/review/${t.token}`);
    load();
  };
  const approve = async (id) => {
    await api.adminApproveReview(id);
    await load();
    // Refresh admin settings so the published list reflects the approval
    await reloadSettings?.();
    onSiteReload?.();
    toast.success("Published");
  };
  const reject  = async (id) => { await api.adminRejectReview(id); await load(); };

  const deleteSamples = async () => {
    const next = items.filter(i => !i.is_sample);
    await saveSettings({ testimonials: { enabled, items: next } });
    toast.success("Samples removed");
  };

  const save = async () => {
    const cleaned = items.map(i => ({
      id: i.id, quote: (i.quote||"").trim(), attribution: (i.attribution||"").trim(),
      featured: !!i.featured, ...(i.is_sample ? { is_sample: true } : {}),
    })).filter(i => i.quote && i.attribution);
    await saveSettings({ testimonials: { enabled, items: cleaned } });
    toast.success("Saved");
  };

  return (
    <div className="space-y-6">
      {sampleCount > 0 && (
        <div className="p-4 rounded-lg border border-yellow-400/40 bg-yellow-400/10 flex items-center justify-between gap-4" data-testid="admin-sample-banner">
          <span className="text-yellow-100 text-sm">
            {sampleCount} SAMPLE testimonial{sampleCount > 1 ? "s" : ""} — hidden from public site.
          </span>
          <button className="btn-ghost-gold !text-[0.7rem]" onClick={deleteSamples} data-testid="admin-delete-samples">Delete all samples</button>
        </div>
      )}

      <Card title="Generate review link" testId="admin-review-generator">
        <p className="text-ivory-dim text-sm mb-3">
          Create a single-use link (expires in 30 days). Send it to a client after their booking to collect a review.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className="input-luxe" placeholder="Client hint (private)" value={hint} onChange={e=>setHint(e.target.value)} data-testid="admin-review-hint" />
          <input className="input-luxe" placeholder="Duration (e.g. 1hr)" value={dur} onChange={e=>setDur(e.target.value)} data-testid="admin-review-duration" />
        </div>
        <button className="btn-gold mt-4" onClick={genLink} data-testid="admin-generate-review-link">Generate review link</button>
      </Card>

      <Card title={`Pending submissions (${pending.length})`} testId="admin-pending-reviews">
        {pending.length === 0 && <p className="text-ivory-dim text-sm italic">Nothing pending.</p>}
        <div className="space-y-3">
          {pending.map(p => (
            <div key={p.id} className="p-4 rounded-lg border border-[rgba(201,169,97,0.2)]" data-testid={`admin-pending-${p.id}`}>
              <p className="font-serif italic text-ivory">"{p.quote}"</p>
              <p className="text-gold-2 text-sm mt-2">— {p.attribution} {p.duration && `• ${p.duration}`}</p>
              <div className="flex gap-2 mt-3">
                <button className="btn-gold !text-[0.7rem] !py-1.5 !px-3" onClick={()=>approve(p.id)} data-testid={`admin-approve-${p.id}`}>Approve</button>
                <button className="btn-ghost-gold !text-[0.7rem] !py-1.5 !px-3" onClick={()=>reject(p.id)} data-testid={`admin-reject-${p.id}`}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Review tokens" testId="admin-review-tokens">
        <div className="space-y-2 text-sm">
          {tokens.slice(0, 20).map(t => (
            <div key={t.token} className="flex items-center justify-between gap-2 py-1.5 border-b border-[rgba(201,169,97,0.1)]">
              <span className="text-ivory-dim truncate">{t.hint || "—"} {t.used && <span className="text-[color:var(--muted)]">· used</span>}</span>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>copy(`${window.location.origin}/review/${t.token}`)}>Copy link</button>
            </div>
          ))}
          {tokens.length===0 && <p className="text-[color:var(--muted)] italic">No tokens yet.</p>}
        </div>
      </Card>

      <Card title="Published testimonials" testId="admin-published-testimonials">
        <div className="flex items-center gap-3 mb-4">
          <button className={enabled?"btn-gold":"btn-ghost-gold"} onClick={()=>setEnabled(true)}>Show section</button>
          <button className={!enabled?"btn-wine":"btn-ghost-gold"} onClick={()=>setEnabled(false)}>Hide</button>
        </div>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={it.id||i} className="p-3 rounded-lg border border-[rgba(201,169,97,0.15)]">
              <div className="flex justify-between mb-2">
                <div className="text-xs text-[color:var(--muted)] uppercase tracking-widest">
                  #{i+1} {it.is_sample && <span className="pill !py-0 !px-2 !text-[0.55rem] ml-2">SAMPLE</span>}
                </div>
                <div className="flex gap-1">
                  <label className="text-xs text-ivory-dim flex items-center gap-1"><input type="checkbox" checked={!!it.featured} onChange={e=>{const cp=[...items];cp[i]={...cp[i],featured:e.target.checked};setItems(cp);}} className="accent-[color:var(--gold)]"/>Featured</label>
                  <button className="btn-ghost-gold !py-1 !px-2 !text-xs" onClick={()=>setItems(items.filter((_,j)=>j!==i))}>✕</button>
                </div>
              </div>
              <textarea className="input-luxe !py-2 mb-2 resize-y" rows={2} value={it.quote} onChange={e=>{const cp=[...items];cp[i]={...cp[i],quote:e.target.value,is_sample:false};setItems(cp);}}/>
              <input className="input-luxe !py-2" value={it.attribution} onChange={e=>{const cp=[...items];cp[i]={...cp[i],attribution:e.target.value,is_sample:false};setItems(cp);}}/>
            </div>
          ))}
        </div>
        <div className="flex justify-end mt-4"><button className="btn-gold" onClick={save} data-testid="admin-save-testimonials">Save</button></div>
      </Card>
    </div>
  );
}

/* ===================== VIP CODES TAB ===================== */
export function VipCodesTab() {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [days, setDays] = useState(30);

  const load = async () => setItems((await api.adminListVipCodes()).items || []);
  useEffect(() => { load(); }, []);

  const create = async () => {
    const r = await api.adminCreateVipCode({ label, max_uses: Number(maxUses)||1, expires_days: Number(days)||null });
    setLabel(""); await load(); copy(r.code);
  };
  const del = async (c) => { if (window.confirm(`Revoke ${c}?`)) { await api.adminDeleteVipCode(c); load(); } };

  return (
    <div className="space-y-6">
      <Card title="Generate VIP unlock code" testId="admin-vip-code-generator">
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <input className="input-luxe" placeholder="Label (e.g. Regular John)" value={label} onChange={e=>setLabel(e.target.value)} data-testid="admin-vip-code-label"/>
          <input className="input-luxe" type="number" min={1} placeholder="Max uses" value={maxUses} onChange={e=>setMaxUses(e.target.value)}/>
          <input className="input-luxe" type="number" min={0} placeholder="Expiry (days)" value={days} onChange={e=>setDays(e.target.value)}/>
        </div>
        <button className="btn-gold" onClick={create} data-testid="admin-generate-vip-code">Generate code</button>
      </Card>
      <Card title={`Active codes (${items.length})`} testId="admin-vip-codes-list">
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.code} className="flex items-center gap-3 p-2 rounded border border-[rgba(201,169,97,0.15)]" data-testid={`admin-vip-code-${c.code}`}>
              <span className="font-mono text-gold text-lg tracking-widest">{c.code}</span>
              <span className="text-ivory-dim text-sm flex-1 truncate">{c.label || "—"}</span>
              <span className="text-xs text-[color:var(--muted)]">{c.used_count}/{c.max_uses||1}</span>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>copy(c.code)}>Copy</button>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>del(c.code)}>Revoke</button>
            </div>
          ))}
          {items.length===0 && <p className="text-[color:var(--muted)] italic text-sm">No codes yet.</p>}
        </div>
      </Card>
    </div>
  );
}

/* ===================== REFERRALS TAB ===================== */
export function ReferralsTab() {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [discount, setDiscount] = useState(50);
  const [days, setDays] = useState(90);

  const load = async () => setItems((await api.adminListReferrals()).items || []);
  useEffect(() => { load(); }, []);

  const create = async () => {
    const r = await api.adminCreateReferral({ label, discount_aud: Number(discount)||50, expires_days: Number(days)||null });
    setLabel(""); await load(); copy(r.code);
  };
  const markUsed = async (c) => {
    const note = window.prompt("Note for this redemption (optional)") || "";
    await api.adminMarkReferralUsed(c, note); load();
  };
  const del = async (c) => { if (window.confirm(`Delete ${c}?`)) { await api.adminDeleteReferral(c); load(); } };

  return (
    <div className="space-y-6">
      <Card title="New referral code" testId="admin-referral-generator">
        <div className="grid sm:grid-cols-3 gap-3 mb-3">
          <input className="input-luxe" placeholder="Label" value={label} onChange={e=>setLabel(e.target.value)} data-testid="admin-referral-label"/>
          <input className="input-luxe" type="number" placeholder="Discount AUD" value={discount} onChange={e=>setDiscount(e.target.value)}/>
          <input className="input-luxe" type="number" placeholder="Expiry (days)" value={days} onChange={e=>setDays(e.target.value)}/>
        </div>
        <button className="btn-gold" onClick={create} data-testid="admin-generate-referral">Generate referral code</button>
      </Card>
      <Card title={`Referral codes (${items.length})`} testId="admin-referral-list">
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.code} className="flex items-center gap-3 p-2 rounded border border-[rgba(201,169,97,0.15)]" data-testid={`admin-referral-${c.code}`}>
              <span className="font-mono text-gold tracking-wider">{c.code}</span>
              <span className="text-gold-2 text-sm">${c.discount_aud}</span>
              <span className="text-ivory-dim text-sm flex-1 truncate">{c.label || "—"}</span>
              <span className="text-xs text-[color:var(--muted)]">{(c.used_by_bookings||[]).length} redeemed</span>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>copy(c.code)}>Copy</button>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>markUsed(c.code)}>Mark used</button>
              <button className="btn-ghost-gold !text-[10px] !py-1 !px-2" onClick={()=>del(c.code)}>✕</button>
            </div>
          ))}
          {items.length===0 && <p className="text-[color:var(--muted)] italic text-sm">No codes yet.</p>}
        </div>
      </Card>
    </div>
  );
}

/* ===================== CLICKS ANALYTICS CARD (goes on Dashboard) ===================== */
export function ClickAnalyticsCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api.adminAnalyticsClicks(days).then(setData).catch(()=>setData(null)); }, [days]);
  const wa = data?.whatsapp || {total:0,by_source:{}};
  const of_ = data?.onlyfans || {total:0,by_source:{}};
  const ph = data?.phone || {total:0,by_source:{}};
  return (
    <Card testId="admin-clicks-card" title="Click analytics"
      action={
        <div className="flex gap-1">
          {[7,30,90,3650].map(d => (
            <button key={d} onClick={()=>setDays(d)} className={`px-2.5 py-1 text-[0.7rem] tracking-wider uppercase rounded-full border ${days===d?"border-gold text-gold":"border-transparent text-ivory-dim hover:text-gold-2"}`} data-testid={`admin-clicks-range-${d}`}>
              {d===3650?"All":`${d}d`}
            </button>
          ))}
        </div>
      }>
      <div className="grid sm:grid-cols-3 gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">WhatsApp clicks</div>
          <div className="font-serif text-5xl text-gold" data-testid="admin-clicks-wa-total">{wa.total}</div>
          <ul className="mt-3 space-y-1 text-sm text-ivory-dim">
            {Object.entries(wa.by_source).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([k,v])=>(
              <li key={k} className="flex justify-between"><span>{k}</span><span className="text-gold-2">{v}</span></li>
            ))}
          </ul>
          <p className="text-[10px] text-[color:var(--muted)] mt-3 italic">Which CTA converts hardest → double down.</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">OnlyFans clicks</div>
          <div className="font-serif text-5xl text-gold" data-testid="admin-clicks-of-total">{of_.total}</div>
          <ul className="mt-3 space-y-1 text-sm text-ivory-dim">
            {Object.entries(of_.by_source).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([k,v])=>(
              <li key={k} className="flex justify-between"><span>{k}</span><span className="text-gold-2">{v}</span></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">Phone call taps</div>
          <div className="font-serif text-5xl text-gold" data-testid="admin-clicks-phone-total">{ph.total}</div>
          <ul className="mt-3 space-y-1 text-sm text-ivory-dim">
            {Object.entries(ph.by_source).sort((a,b)=>b[1]-a[1]).slice(0,6).map(([k,v])=>(
              <li key={k} className="flex justify-between"><span>{k}</span><span className="text-gold-2">{v}</span></li>
            ))}
            {ph.total===0 && <li className="text-[color:var(--muted)] italic text-xs">No taps yet.</li>}
          </ul>
          <p className="text-[10px] text-[color:var(--muted)] mt-3 italic">tel: taps from hero / rate card.</p>
        </div>
      </div>
    </Card>
  );
}
